import { useNavigate, Link } from "react-router-dom";
import { CreditCard, ArrowLeftRight, ChevronRight, Inbox, Users, ReceiptText, ArrowDownLeft } from "lucide-react";
import { SubHeader, StatusBadge, EmptyState } from "@/components/Brand";
import { useApi } from "@/lib/api";
import { rp, fmtDateTime, KIND_LABEL, METHOD_LABEL, fmtAcc } from "@/lib/format";

export const RequestItem = ({ r }) => (r.incoming ? <IncomingItem r={r} /> : (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`request-item-${r.req_id}`}>
    <div className="flex justify-between items-start gap-2">
      <div>
        <div className="font-semibold text-sm text-slate-900">{KIND_LABEL[r.kind]}</div>
        <div className="text-[11px] text-slate-500">{fmtDateTime(r.created_at)} • {METHOD_LABEL[r.method]}</div>
      </div>
      <StatusBadge status={r.status} />
    </div>
    <div className="flex justify-between items-end mt-2">
      <div className="text-xs text-slate-600">
        {r.kind === "transfer_bank" && `${r.bank_name} • ${r.account_no} • ${r.recipient_name}`}
        {r.kind === "transfer_member" && `${r.recipient_name} (${r.target_account_no ? fmtAcc(r.target_account_no) : r.target_member_no})`}
        {r.kind === "setoran" && `Simpanan ${r.savings_type}`}
      </div>
      <div className="font-bold text-slate-900">{rp(r.amount)}</div>
    </div>
    {r.admin_note && r.kind !== "transfer_member" && <div className="text-xs text-slate-700 mt-2 bg-slate-50 rounded-lg p-2">Catatan admin: {r.admin_note}</div>}
    {r.kind === "transfer_member" && r.status === "disetujui" && r.from_account_no && (
      <Link to={`/app/transaksi/bukti/${r.req_id}`} data-testid={`request-receipt-${r.req_id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:underline">
        <ReceiptText className="h-3.5 w-3.5" /> Lihat Bukti Transfer
      </Link>
    )}
  </div>
));

const IncomingItem = ({ r }) => (
  <div className="bg-white border border-emerald-200 rounded-2xl p-4" data-testid={`incoming-item-${r.req_id}`}>
    <div className="flex justify-between items-start gap-2">
      <div>
        <div className="font-semibold text-sm text-emerald-700 flex items-center gap-1.5"><ArrowDownLeft className="h-4 w-4" />Dana Masuk</div>
        <div className="text-[11px] text-slate-500">{fmtDateTime(r.decided_at || r.created_at)} • Transfer Antar Anggota</div>
      </div>
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Diterima</span>
    </div>
    <div className="flex justify-between items-end mt-2">
      <div className="text-xs text-slate-600">Dari {r.member_name} ({fmtAcc(r.from_account_no)})</div>
      <div className="font-bold text-emerald-600">+{rp(r.amount)}</div>
    </div>
    <Link to={`/app/transaksi/bukti/${r.req_id}`} data-testid={`incoming-receipt-${r.req_id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:underline">
      <ReceiptText className="h-3.5 w-3.5" /> Lihat Bukti Transfer
    </Link>
  </div>
);

export default function Transactions() {
  const navigate = useNavigate();
  const { data } = useApi("/requests");
  const items = [
    { icon: CreditCard, t: "Pembayaran", d: "Angsuran, setoran simpanan, biaya administrasi", to: "/app/transaksi/bayar", id: "payments" },
    { icon: Users, t: "Transfer Antar Anggota", d: "Ke nomor rekening sesama anggota CU", to: "/app/transaksi/transfer?ke=anggota", id: "transfer-member" },
    { icon: ArrowLeftRight, t: "Transfer ke Bank", d: "Ke rekening bank lain (BI-FAST)", to: "/app/transaksi/transfer", id: "transfer" },
  ];
  return (
    <div>
      <SubHeader title="Transaksi" />
      <div className="px-5 space-y-3">
        {items.map(({ icon: Icon, t, d, to, id }) => (
          <button key={id} onClick={() => navigate(to)} data-testid={`transactions-menu-${id}`}
            className="w-full flex items-center gap-4 bg-white border border-slate-200 rounded-2xl p-4 text-left hover:shadow-md transition-shadow">
            <div className="h-11 w-11 rounded-xl bg-red-50 flex items-center justify-center"><Icon className="h-5 w-5 text-red-600" /></div>
            <div className="flex-1"><div className="font-semibold text-sm">{t}</div><div className="text-xs text-slate-500">{d}</div></div>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </button>
        ))}
        <h2 className="font-bold text-slate-900 pt-3">Riwayat Permintaan</h2>
        {data?.length === 0 && <EmptyState testid="requests-empty" icon={Inbox} title="Belum ada transaksi" text="Pembayaran dan transfer Anda akan tampil di sini." />}
        <div className="space-y-2" data-testid="requests-list">
          {data?.map((r) => <RequestItem key={r.req_id} r={r} />)}
        </div>
      </div>
    </div>
  );
}
